import React, { useState, useEffect } from 'react';
import { Bot, Wrench, FileText } from 'lucide-react';
import { AIToolsModal } from './AIToolsModal';
import { getBookingsByCustomer } from '../lib/bookings';
import { useBusiness } from '../contexts/BusinessContext';
import { getAIToolSuggestions, AIToolSuggestion } from '../services/aiToolsService';
import toast from 'react-hot-toast';

interface NexusMessagesAssistantProps {
  businessId: string;
  contactInfo: string;
  messageType: 'sms' | 'email';
  customerId?: string;
  conversationId?: string;
  isVisible: boolean;
  onQuickActionExecuted: (action: any, result: any) => void;
  onOpenBookingModal?: (booking: any) => void;
  refreshTrigger?: number; // Optional prop to trigger AI suggestions refresh
}

export const NexusMessagesAssistant: React.FC<NexusMessagesAssistantProps> = ({
  businessId,
  contactInfo,
  messageType,
  customerId,
  conversationId,
  isVisible,
  onQuickActionExecuted,
  onOpenBookingModal,
  refreshTrigger
}) => {
  const { selectedBusiness } = useBusiness();
  const [isAIToolsModalOpen, setIsAIToolsModalOpen] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AIToolSuggestion[]>([]);

  // Get AI suggestions when component mounts or conversation changes
  useEffect(() => {
    const fetchAISuggestions = async () => {
      if (!selectedBusiness?.id || !contactInfo || !isVisible) {
        return;
      }

      try {
        const suggestions = await getAIToolSuggestions(
          selectedBusiness.id,
          contactInfo,
          messageType,
          customerId
        );
        setAiSuggestions(suggestions);
        console.log('🤖 AI suggestions updated:', suggestions);
      } catch (error) {
        console.error('❌ Error fetching AI suggestions:', error);
        setAiSuggestions([]);
      }
    };

    fetchAISuggestions();
  }, [selectedBusiness?.id, contactInfo, messageType, customerId, isVisible, refreshTrigger]);

  const handleInvoiceTool = async () => {
    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    if (!customerId) {
      toast.error('Customer not identified');
      return;
    }

    setIsLoadingInvoice(true);
    
    try {
      // Get customer's most recent booking using the customerId
      const bookings = await getBookingsByCustomer(selectedBusiness.id, customerId, { limit: 1 });
      
      if (!bookings || bookings.length === 0) {
        toast.error('No bookings found for this customer');
        return;
      }

      const mostRecentBooking = bookings[0];
      
      // Call parent callback to open payments modal
      if (onOpenBookingModal) {
        onOpenBookingModal(mostRecentBooking);
      }
      
      toast.success('Opening payments for most recent booking');
      
    } catch (error) {
      console.error('Error in Invoice tool:', error);
      toast.error('Failed to find booking. Please try again.');
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="flex items-center justify-between h-8 px-2">
      {/* Left side - Bot icon + Tools */}
      <div className="flex items-center space-x-2">
        <Bot className="h-4 w-4 text-gray-500" />
        <span className="text-xs text-gray-400">Nexus</span>
        
        {/* AI-Suggested Tools - appear after Nexus branding */}
        <div className="flex items-center space-x-1 ml-2">
          {aiSuggestions.map((suggestion) => {
            if (suggestion.type === 'send_invoice') {
              return (
                <button
                  key={suggestion.id}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 text-sm font-medium ${
                    !customerId 
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60' 
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-sm hover:shadow-md'
                  }`}
                  title={!customerId ? 'Customer not identified' : isLoadingInvoice ? 'Finding booking...' : suggestion.description}
                  onClick={handleInvoiceTool}
                  disabled={isLoadingInvoice || !customerId}
                >
                  <FileText className="h-4 w-4" />
                  <span>{isLoadingInvoice ? 'Loading...' : suggestion.label}</span>
                </button>
              );
            }
            
            // Add other tool types here in the future
            return null;
          })}
        </div>
      </div>

      {/* Right side - System Actions */}
      <div className="flex items-center space-x-1">
        {/* AI Tools Button */}
        <button
          className="flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200 group"
          title="AI Tools"
          onClick={() => setIsAIToolsModalOpen(true)}
          style={{ display: 'none' }}
        >
          <Wrench className="h-4 w-4 text-gray-600 group-hover:text-gray-800" />
        </button>
      </div>
      
      {/* AI Tools Modal */}
      <AIToolsModal
        isOpen={isAIToolsModalOpen}
        onClose={() => setIsAIToolsModalOpen(false)}
        contactInfo={contactInfo}
        messageType={messageType}
        customerId={customerId}
        onOpenBookingModal={onOpenBookingModal}
      />
    </div>
  );
};